import { POSSIBLE } from './prompts.js';
import { CEILING, rungFor } from './standing.js';

/**
 * Charm.
 *
 * One verb, a long cooldown, and it works on anybody — a guest wondering what
 * you are doing out here, a manager who thinks he saw something. What it does
 * not do is work on somebody who has already made up their mind: past ACCUSING
 * they are not listening, and trying it hardens them, because turning on the
 * charm at the person walking off to report you is the most obviously guilty
 * thing there is.
 *
 * There is no romance layer. What there *is*, now that colleagues have their
 * own opinion of you, is somewhere for it to land — and the rule that makes it
 * worth having is that it **repairs and does not build**. The gain falls away
 * as somebody already thinks well of you, so a word in the corridor is how you
 * get out of the cold and it is not how you get to the top of anybody's ladder.
 * That is what turning up when a favour is called in is for.
 *
 * And it is not free. Three things it costs:
 *
 *   - the cooldown, which was always there
 *   - **minutes**, off the shift, on the clock, in the currency the whole
 *     middle game is priced in. Having a word with somebody takes as long as it
 *     takes, and the deadline does not wait for it
 *   - and being wrong about it. Past ACCUSING they are not listening, they are
 *     looking for a manager, and a charm offensive aimed at somebody who has
 *     already decided hardens them *and* costs you with them personally. Which
 *     is the most obviously guilty thing a person can do, priced accordingly.
 */
export function createCharm({ regard, standing, suspicion, clock, hud, autosave, npcs, think, nearestNpc, elapsed, unavailable = () => false }) {
  const CHARM_COOLDOWN = 45;
  /** Game minutes a word costs. Roughly a third of the shortest errand on the roster. */
  const CHARM_MINUTES = 5;
  /** What it is worth to somebody who thinks nothing of you either way. */
  const CHARM_REGARD = 18;
  /** And what it costs to try it on somebody who has already made up their mind. */
  const CHARM_BACKFIRE = -20;
  let charmedAt = -Infinity;

  /**
   * Diminishing, against what they already think.
   *
   * Full value at nothing and nearly none at the top, so the ladder's upper half
   * cannot be talked into existence. Below nothing it does not *grow* — a person
   * who cannot stand you is not more susceptible to being charmed, they are
   * simply further away — so the gain is capped at its full value and the climb
   * out of a hole is long on purpose.
   */
  function charmWorth(id) {
    const now = regard.get(id);
    return Math.round(CHARM_REGARD * Math.min(1, 1 - Math.max(0, now) / CEILING));
  }

  function offerCharm() {
    if (unavailable()) return null;
    const near = nearestNpc();
    if (!near) return null;
    const cooling = Math.max(0, CHARM_COOLDOWN - (elapsed() - charmedAt));
    return {
      id: 'charm',
      tier: POSSIBLE,
      distance: near.distance,
      verb: 'have a word with',
      what: near.npc.data.name,
      blocked: cooling > 0 ? `not again for ${Math.ceil(cooling)}s` : null,
      run: () => haveAWord(near.npc),
    };
  }

  /**
   * Which version of the pitch you can get away with.
   *
   * Two, and the difference between them is the whole reason charm reads as a
   * person rather than as a button. To somebody who has no time for you it is an
   * excuse, offered to a stranger. To somebody who does it is shorter, because it
   * does not have to do any work: you say their name and they already know what
   * you mean. Read off whichever ladder this person is on, the same as a
   * greeting.
   */
  function pitchAt(npc) {
    const id = npc.data.id;
    const where = regard.ids.includes(id) ? regard.get(id) : standing.get(npc.data.department);
    const rung = rungFor(where).id;
    return rung === 'warm' || rung === 'trusted' ? 'warm' : 'cold';
  }

  function haveAWord(npc) {
    charmedAt = elapsed();
    const who = npc.data.id;
    const mine = regard.ids.includes(who);
    const worth = mine ? charmWorth(who) : 0;
    const result = suspicion.charm(who);

    // The minutes go whether it worked or not. You still stood there talking.
    clock.skip(CHARM_MINUTES);

    if (mine) {
      think(
        who,
        result.ok ? worth : CHARM_BACKFIRE,
        result.ok ? `You had a word with ${npc.data.name}` : `${npc.data.name} was not having it`
      );
    }
    const charm = npc.data.charm;
    const back = charm?.[result.ok ? 'ok' : 'no'];
    npc.say(back ?? (result.ok ? 'hears you out' : 'is not having it'), 4);

    const tried = charm?.[pitchAt(npc)];
    const spent = `${CHARM_MINUTES} minutes gone.`;
    hud.say(
      [
        tried ? `You: "${tried}"` : null,
        back ? `${npc.data.name}: "${back}"` : `${npc.data.name} ${result.ok ? 'lets it go' : 'is not interested'}.`,
        result.ok ? spent : `That did not help. ${spent}`,
        result.ok && mine && worth <= 2 ? 'They already thought well of you.' : null,
      ]
        .filter(Boolean)
        .join(' '),
      { tone: result.ok ? 'good' : 'bad', seconds: 6 }
    );
    autosave.touch();
  }

  return {
    offerCharm,
    haveAWord,
    pitchAt,
    charmWorth,
    /**
     * Has a word with somebody, exactly as the key does, and reports back.
     * Headless only: the readouts that measure what a word is worth.
     */
    headless(id) {
      const npc = npcs.find((candidate) => candidate.data.id === id);
      if (!npc) return null;
      const before = regard.ids.includes(id) ? regard.get(id) : null;
      const minutes = clock.minutes;
      const cooling = Math.max(0, CHARM_COOLDOWN - (elapsed() - charmedAt));
      if (cooling > 0) return { ok: false, cooling: Math.ceil(cooling) };
      charmedAt = elapsed();
      const worth = regard.ids.includes(id) ? charmWorth(id) : 0;
      const result = suspicion.charm(id);
      clock.skip(CHARM_MINUTES);
      if (regard.ids.includes(id)) think(id, result.ok ? worth : CHARM_BACKFIRE, 'headless');
      return {
        ok: result.ok,
        before,
        after: regard.ids.includes(id) ? regard.get(id) : null,
        minutes: clock.minutes - minutes,
        cooling: 0,
        /** The two halves of it, so a readout never has to type one of them. */
        said: npc.data.charm?.[pitchAt(npc)] ?? null,
        back: npc.data.charm?.[result.ok ? 'ok' : 'no'] ?? null,
      };
    },
    /** Clears the cooldown, so a readout can ask more than one question. */
    uncharm() {
      charmedAt = -Infinity;
    },
  };
}
